export const siteInfo = {
  name: 'Shaked Gofin',
  title: 'AI Engineer & Researcher',
  subtitle: 'Healthcare AI · Multi-Agent Systems · LLM Applications',
  location: 'Austin, TX',
  tagline:
    'Building AI platforms for clinical research with Dell Medical School, MD Anderson, and the UT School of Nursing.',
  email: 'sgofin@gmail.com',
  github: 'https://github.com/Shaked-g',
  linkedin: 'https://linkedin.com/in/shaked-gofin',
  resumeUrl: '/Shaked-Gofin-Resume.pdf',
}

export const aboutParagraphs = [
  'I am an AI engineer and researcher at UT Austin\'s AI Health Lab, where I design and deploy multi-agent systems and LLM applications used in active clinical research. My work spans explainable healthcare AI, retrieval-augmented generation over clinical guidelines, and voice-driven medical training platforms.',
  'With 4+ years of industry experience across machine learning, full-stack development, and cloud infrastructure at Selectika AI and in competitive startup programs, I bring both research rigor and production engineering to every project.',
  'Bilingual in English and Hebrew. Authorized to work in the USA.',
]

export const navSections = [
  'home',
  'about',
  'experience',
  'projects',
  'skills',
  'education',
  'contact',
] as const

export type NavSection = (typeof navSections)[number]

export interface Experience {
  title: string
  company: string
  date: string
  description: string[]
  highlights?: string[]
}

export const experience: Experience[] = [
  {
    title: 'Senior Research Scientist',
    company: 'UT Austin, AI Health Lab',
    date: 'Sep 2025 to Present',
    highlights: ['AMIA 2026', 'MASCC', 'NSF $26.7M'],
    description: [
      'Built Heart Health Companion, a multi-agent AI system for heart failure patients with 7 specialized agents under a central orchestrator, full explainability traces, persona-adapted coaching, clinical safety contracts, and architecture supporting up to 100 concurrent user sessions',
      'Designed a RAG pipeline over AHA/HFSA clinical guidelines using 70/30 hybrid semantic and keyword search, medical synonym expansion, and embedding-based caching in MongoDB',
      'Implemented OAuth wearable integrations (Fitbit, Withings, Google Fit, Health Connect) with background sync feeding multi-window health trend analysis, daily check-ins, shareable clinician reports, and gamification',
      'Deployed production backend on AWS App Runner with DocumentDB and multi-provider LLM routing (OpenAI, OpenRouter, AWS Bedrock) with per-request usage and cost tracking',
      'Built a full-stack medical communication training platform end to end, implementing real-time LLM evaluation against clinician-designed 48-item research and 8-item GAIA OSCE rubrics, plus ElevenLabs voice-to-voice patient simulation, quote-anchored feedback, and conversation replay on GCP Cloud Run for Dell Medical School research',
      'Developed the DreamKG conversational interface: a Next.js command center with natural-language intake into structured spatial Neo4j queries, streaming outreach chat, interactive graph explorer, and explainable match validation for the NSF Proto-OKN ecosystem',
    ],
  },
  {
    title: 'Google Startup Competition',
    company: 'Israeli Ministry of Health',
    date: 'Mar 2024 to Jun 2024',
    description: [
      'Built a RAG-based AI chatbot using Google Vertex AI, LangChain, and Node.js, containerized with Docker and deployed to Google Cloud Run with auto-scaling',
    ],
  },
  {
    title: 'Machine Learning Developer',
    company: 'Selectika AI, Tel Aviv',
    date: 'Aug 2022 to May 2023',
    description: [
      'Integrated computer vision APIs and OpenCV into the product pipeline for real-time image classification, connecting ML models to databases and front-end components',
      'Ran feature engineering experiments to improve predictive model accuracy by analyzing feature importance and testing feature combinations',
      'Restructured the database architecture to speed up analytical queries and support ML-driven insights across the product',
      'Reduced cloud compute costs by right-sizing AWS EC2 instances and optimizing resource allocation',
    ],
  },
  {
    title: 'Full Stack Developer',
    company: 'Selectika AI, Tel Aviv',
    date: 'Aug 2021 to Aug 2022',
    description: [
      'Owned all client-facing data pipelines from ingestion through transformation to delivery',
      'Built and maintained RESTful APIs using Express.js, Node.js, and MongoDB for the core product',
      'Handled end-to-end feature development and deployment through AWS EC2 and S3/CloudFront CDN',
    ],
  },
]

export type ProjectCategory = 'Research' | 'Industry' | 'Personal'

export interface Project {
  title: string
  description: string
  tech: string[]
  category: ProjectCategory
  badges?: string[]
  impact?: string
  link?: string
}

export const featuredProjects: Project[] = [
  {
    title: 'Multi-Agent Heart Failure Platform',
    description:
      'Heart Health Companion: a seven-agent system for heart failure self-management spanning clinical education, behavior coaching, health tracking, emotional support, and care navigation. Includes explainability traces, clinical safety guardrails, persona-adapted coaching, wearable sync (Fitbit, Withings, Google Fit, Health Connect), and a daily engagement loop with trend dashboards and shareable clinician reports.',
    tech: [
      'Python',
      'Flask',
      'React',
      'TypeScript',
      'Multi-Agent Systems',
      'RAG',
      'AWS Bedrock',
      'AWS App Runner',
      'Capacitor',
      'MongoDB',
      'DocumentDB',
    ],
    category: 'Research',
    badges: ['AMIA 2026'],
    impact: 'Architecture supporting up to 100 concurrent sessions; 133+ routing and safety tests',
  },
  {
    title: 'AI Medical Communication Training Platform',
    description:
      'Full-stack platform where medical trainees practice patient encounters through AI-simulated voice and text sessions. Built end to end and deployed on GCP Cloud Run for Dell Medical School clinical communication research, implementing LLM evaluation pipelines for clinician-designed 48-item research and 8-item GAIA OSCE rubrics with quote-anchored feedback, ElevenLabs voice patients, and conversation replay.',
    tech: [
      'Node.js',
      'Express',
      'TypeScript',
      'React',
      'MongoDB',
      'OpenAI',
      'ElevenLabs',
      'SSE',
      'Docker',
      'Google Cloud Run',
    ],
    category: 'Research',
    badges: ['MASCC', 'Dell Medical School'],
    impact: 'Live and streaming LLM rubric evaluation implemented across 48 research criteria',
  },
  {
    title: 'DreamKG Conversational Interface',
    description:
      'Modern interface layer for the NSF Proto-OKN DreamKG ecosystem: a Next.js command center where social workers search homeless-services resources via natural-language intake into deterministic spatial Neo4j Cypher, with streaming AI outreach chat, an interactive knowledge-graph explorer, and SHACL-style explainable match validation.',
    tech: [
      'TypeScript',
      'Next.js',
      'React',
      'Vercel AI SDK',
      'Neo4j',
      'Knowledge Graphs',
      'Google Maps',
      'Tailwind CSS',
    ],
    category: 'Research',
    badges: ['NSF Proto-OKN'],
    impact: 'NL constraint extraction plus spatial graph search without brittle LLM-generated Cypher',
  },
  {
    title: 'Clinical Guidelines RAG Pipeline',
    description:
      'Hybrid retrieval (70/30 semantic and keyword) over AHA/HFSA and low-literacy guideline chunks stored in MongoDB, with medical synonym query expansion (e.g. SOB, lasix, entresto) and embedding cache for repeated clinical queries. Powers the HF Educator agent within Heart Health Companion.',
    tech: ['Python', 'RAG', 'Embeddings', 'Hybrid Search', 'MongoDB', 'OpenAI Embeddings'],
    category: 'Research',
    impact: 'Structured medication knowledge base alongside clinical guidelines',
  },
  {
    title: 'MoH AI Chatbot',
    description:
      'RAG-based AI chatbot for the Israeli Ministry of Health built during the Google Startup Competition, deployed on Google Cloud Run with auto-scaling.',
    tech: ['LangChain', 'Vertex AI', 'Node.js', 'Docker', 'Google Cloud Run'],
    category: 'Industry',
    badges: ['Google Startup Competition'],
  },
]

export const additionalProjects: Project[] = [
  {
    title: 'Digital Time Vault',
    description:
      'Secure platform enabling users to lock and schedule files for future access with end-to-end encryption and cloud storage integration.',
    tech: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Google Cloud Storage', 'JWT'],
    category: 'Personal',
    link: 'https://mytime-capsule.netlify.app',
  },
  {
    title: 'EFI Machine Learning Analysis',
    description:
      'Classification and clustering models applied to patient data to identify key insights into Enteral Feeding Intolerance.',
    tech: ['Python', 'Scikit-learn', 'Pandas', 'NumPy'],
    category: 'Research',
    link: 'https://github.com/Shaked-g/FinalProject',
  },
]

export const skillGroups = [
  {
    label: 'Languages',
    skills: ['Python', 'TypeScript', 'JavaScript', 'C', 'C++', 'Bash'],
  },
  {
    label: 'Generative AI & LLMs',
    skills: [
      'LangChain',
      'RAG (hybrid search, embeddings, synonym expansion)',
      'Multi-Agent Systems (orchestration, tool use)',
      'LLMs / Prompt Engineering',
      'NLP',
      'Vector Search / Embeddings',
      'Knowledge Graphs',
      'Clinical safety guardrails & explainability traces',
      'Persona-adaptive dialogue systems',
      'Model Evaluation (rubric implementation, explainability traces)',
    ],
  },
  {
    label: 'Machine Learning',
    skills: [
      'PyTorch',
      'TensorFlow',
      'Scikit-learn',
      'OpenCV',
      'Hugging Face Transformers',
      'FAISS',
      'Weights & Biases',
    ],
  },
  {
    label: 'ML Engineering',
    skills: [
      'FastAPI',
      'Flask',
      'REST APIs (ML inference endpoints)',
      'Node.js',
      'Express.js',
      'Next.js',
      'React',
      'Capacitor (cross-platform mobile)',
      'Wearable health API integrations (Fitbit, Withings, Health Connect)',
    ],
  },
  {
    label: 'Data',
    skills: ['Pandas', 'NumPy', 'MongoDB', 'Neo4j', 'SQL', 'Health telemetry ingestion & trend analysis'],
  },
  {
    label: 'Cloud & DevOps',
    skills: [
      'AWS (EC2, S3, SageMaker, Bedrock, App Runner)',
      'Amazon DocumentDB',
      'GCP (Cloud Run, Vertex AI, Cloud Storage)',
      'Vercel AI SDK',
      'ElevenLabs',
      'Docker',
      'GitHub Actions',
    ],
  },
]

export const certifications = [
  'AWS Certified AI Practitioner',
  'AWS Certified Machine Learning Engineer Associate',
  'Generative AI with Large Language Models',
  'Node JS Advanced Concepts',
]

export interface Education {
  degree: string
  school: string
  date: string
  details?: string[]
  coursework?: string[]
}

export const education: Education[] = [
  {
    degree: 'M.S. in Artificial Intelligence',
    school: 'University of Texas at Austin',
    date: 'Aug 2025 to Jun 2027',
  },
  {
    degree: 'B.S. in Computer Science',
    school: 'Ariel University',
    date: 'Oct 2019 to Mar 2024',
    details: ['Minor in Mathematics', 'Track: Data Science and Artificial Intelligence'],
    coursework: [
      'Machine Learning',
      'Deep Learning',
      'Computer Vision',
      'Natural Language Processing',
      'Data Structures',
      'Algorithms',
      'Database Systems',
      'Linear Algebra',
      'Statistics',
      'Probability Theory',
    ],
  },
]
